/**
 * RightSidebar Component
 *
 * Desktop-only collapsible right sidebar panel for contextual content.
 * Features:
 * - Collapsible: 280px expanded, 0px collapsed (hidden when collapsed)
 * - Toggle button: Chevron icon on left edge to expand/collapse
 * - Smooth animation: transition-all duration-200
 * - Content sections: Tasks, Links, Tags, Related Info
 * - Design: Matches existing dark theme
 * - Desktop only: hidden md:flex
 *
 * State is managed via useUIStore for persistence across sessions.
 */

import React, { useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square,
  Link as LinkIcon,
  Tag,
  Info,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from 'lucide-react';
import { useUIStore } from '@shared/store/uiStore';

/**
 * Task item interface
 */
interface Task {
  id: string;
  label: string;
  completed: boolean;
}

/**
 * Link item interface
 */
interface LinkItem {
  id: string;
  label: string;
  url: string;
}

/**
 * Tag item interface
 */
interface TagItem {
  id: string;
  label: string;
  color?: 'default' | 'blue' | 'green' | 'yellow' | 'red' | 'purple';
}

/**
 * Related info section interface
 */
interface RelatedSection {
  id: string;
  title: string;
  content: React.ReactNode;
}

export interface RightSidebarProps {
  /** List of tasks to display */
  tasks?: Task[];
  /** Callback when task is toggled */
  onTaskToggle?: (taskId: string, completed: boolean) => void;
  /** List of links to display */
  links?: LinkItem[];
  /** List of tags to display */
  tags?: TagItem[];
  /** Related info sections (collapsible accordions) */
  relatedSections?: RelatedSection[];
  /** Custom content to render instead of default sections */
  children?: React.ReactNode;
}

/**
 * Accordion section component for collapsible content
 */
const AccordionSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, icon, defaultOpen = true, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-dark-border/30 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-dark-text hover:bg-dark-surface/50 transition-all duration-150"
        aria-expanded={isOpen}
      >
        <span className="flex items-center gap-2">
          {icon}
          {title}
        </span>
        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {isOpen && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

/**
 * Tag color mapping
 */
const tagColorClasses: Record<NonNullable<TagItem['color']>, string> = {
  default: 'bg-dark-surface/50 text-dark-text border-dark-border/30',
  blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  green: 'bg-green-500/20 text-green-400 border-green-500/30',
  yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  red: 'bg-red-500/20 text-red-400 border-red-500/30',
  purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

export const RightSidebar: React.FC<RightSidebarProps> = ({
  tasks = [],
  onTaskToggle,
  links = [],
  tags = [],
  relatedSections = [],
  children,
}) => {
  const { rightSidebarExpanded, toggleRightSidebar, focusMode } = useUIStore();

  // Demo data for when no props are provided
  const demoTasks: Task[] = tasks.length > 0 ? tasks : [
    { id: '1', label: 'Review campaign settings', completed: false },
    { id: '2', label: 'Update contact list', completed: true },
    { id: '3', label: 'Schedule follow-up emails', completed: false },
  ];

  const demoLinks: LinkItem[] = links.length > 0 ? links : [
    { id: '1', label: 'Documentation', url: '#' },
    { id: '2', label: 'API Reference', url: '#' },
    { id: '3', label: 'Support', url: '#' },
  ];

  const demoTags: TagItem[] = tags.length > 0 ? tags : [
    { id: '1', label: 'Active', color: 'green' },
    { id: '2', label: 'Priority', color: 'red' },
    { id: '3', label: 'Campaign', color: 'blue' },
  ];

  const displayTasks = tasks.length > 0 ? tasks : demoTasks;
  const displayLinks = links.length > 0 ? links : demoLinks;
  const displayTags = tags.length > 0 ? tags : demoTags;

  // Hide completely when focus mode is active
  if (focusMode) {
    return null;
  }

  return (
    <>
      {/* Toggle Button - Always visible on desktop when collapsed */}
      <button
        onClick={toggleRightSidebar}
        className={`
          hidden md:flex items-center justify-center
          fixed right-0 top-1/2 -translate-y-1/2 z-40
          w-6 h-12 bg-dark-surface/90 backdrop-blur-sm border border-dark-border/30 border-r-0
          rounded-l-lg text-dark-muted hover:text-white hover:bg-dark-surface
          transition-all duration-200 ease-out shadow-sm hover:shadow-md
          ${rightSidebarExpanded ? 'right-[280px]' : 'right-0'}
        `}
        title={rightSidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
        aria-label={rightSidebarExpanded ? 'Collapse right sidebar' : 'Expand right sidebar'}
        data-testid="right-sidebar-toggle"
        data-ux="action"
      >
        {rightSidebarExpanded ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      {/* Sidebar Panel */}
      <div
        className={`
          hidden md:flex flex-col bg-gradient-to-b from-dark-surface/95 to-dark-bg/90 backdrop-blur-sm border-l border-dark-border/25
          shrink-0 transition-all duration-200 ease-out overflow-hidden
          ${rightSidebarExpanded ? 'w-[280px]' : 'w-0'}
        `}
        aria-hidden={!rightSidebarExpanded}
      >
        {rightSidebarExpanded && (
          <div className="flex flex-col h-full overflow-y-auto overflow-x-hidden overscroll-contain">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-dark-border/25 shrink-0 bg-gradient-to-r from-dark-surface/40 to-transparent">
              <span className="text-xs font-bold text-white/70 uppercase tracking-wider">Context Panel</span>
            </div>

            {/* Custom children or default sections */}
            {children ? (
              <div className="flex-1 overflow-y-auto">{children}</div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                {/* Tasks Section */}
                <AccordionSection
                  title="Tasks"
                  icon={<CheckSquare size={14} />}
                  defaultOpen={true}
                >
                  <div className="space-y-1">
                    {displayTasks.map((task) => (
                      <button
                        key={task.id}
                        onClick={() => onTaskToggle?.(task.id, !task.completed)}
                        className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded text-sm hover:bg-dark-surface/50 transition-all duration-150"
                        data-ux="action"
                      >
                        {task.completed ? (
                          <CheckSquare size={14} className="text-green-500 shrink-0" />
                        ) : (
                          <Square size={14} className="text-dark-muted shrink-0" />
                        )}
                        <span
                          className={`truncate ${
                            task.completed ? 'text-dark-muted line-through' : 'text-dark-text'
                          }`}
                        >
                          {task.label}
                        </span>
                      </button>
                    ))}
                    {displayTasks.length === 0 && (
                      <p className="text-xs text-dark-muted px-2">No tasks</p>
                    )}
                  </div>
                </AccordionSection>

                {/* Links Section */}
                <AccordionSection
                  title="Links"
                  icon={<LinkIcon size={14} />}
                  defaultOpen={true}
                >
                  <div className="space-y-1">
                    {displayLinks.map((link) => (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded text-sm text-dark-text hover:bg-dark-surface/50 hover:text-brand-400 transition-all duration-150"
                        data-ux="action"
                      >
                        <ExternalLink size={12} className="text-dark-muted shrink-0" />
                        <span className="truncate">{link.label}</span>
                      </a>
                    ))}
                    {displayLinks.length === 0 && (
                      <p className="text-xs text-dark-muted px-2">No links</p>
                    )}
                  </div>
                </AccordionSection>

                {/* Tags Section */}
                <AccordionSection
                  title="Tags"
                  icon={<Tag size={14} />}
                  defaultOpen={true}
                >
                  <div className="flex flex-wrap gap-1.5">
                    {displayTags.map((tag) => (
                      <span
                        key={tag.id}
                        className={`
                          inline-flex items-center px-2 py-0.5 rounded text-xs border
                          ${tagColorClasses[tag.color || 'default']}
                        `}
                      >
                        {tag.label}
                      </span>
                    ))}
                    {displayTags.length === 0 && (
                      <p className="text-xs text-dark-muted">No tags</p>
                    )}
                  </div>
                </AccordionSection>

                {/* Related Info Sections */}
                {relatedSections.map((section) => (
                  <AccordionSection
                    key={section.id}
                    title={section.title}
                    icon={<Info size={14} />}
                    defaultOpen={false}
                  >
                    {section.content}
                  </AccordionSection>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default RightSidebar;
